import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Phone as PhoneIcon,
  Handshake as ManagerIcon
} from '@mui/icons-material';
import { useDraggable } from '@dnd-kit/core';
import { getDueChipProps } from '../../utils/dueDate';

const MetaRow = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
    {icon}
    <Typography sx={{ fontSize: '0.75rem' }} noWrap>
      {children}
    </Typography>
  </Box>
);

/**
 * Kanban card for a lead. Draggable between status columns; clicking opens
 * the lead dialog (the DnD pointer sensor uses an activation distance so
 * plain clicks don't start a drag).
 */
const LeadCard = ({ lead, managerName, onClick, overlay = false }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    disabled: overlay
  });

  const followup = getDueChipProps(lead.nextFollowupAt);

  return (
    <Paper
      ref={overlay ? undefined : setNodeRef}
      {...(overlay ? {} : listeners)}
      {...(overlay ? {} : attributes)}
      onClick={overlay ? undefined : onClick}
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        cursor: overlay ? 'grabbing' : 'pointer',
        boxShadow: overlay
          ? '0 8px 20px rgba(9,30,66,0.35), 0 0 1px rgba(9,30,66,0.31)'
          : '0 1px 1px rgba(9,30,66,0.25), 0 0 1px rgba(9,30,66,0.31)',
        transition: 'box-shadow 150ms',
        userSelect: 'none',
        touchAction: 'none',
        opacity: isDragging ? 0 : 1,
        bgcolor: '#fff',
        '&:hover': overlay ? {} : {
          boxShadow: '0 4px 8px -2px rgba(9,30,66,0.25), 0 0 1px rgba(9,30,66,0.31)'
        }
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, wordWrap: 'break-word', mb: 0.25 }}>
        {lead.businessName}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 1 }} noWrap>
        {lead.contactName}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {managerName && (
          <MetaRow icon={<ManagerIcon sx={{ fontSize: 12, flexShrink: 0 }} />}>{managerName}</MetaRow>
        )}
        {lead.phone && (
          <MetaRow icon={<PhoneIcon sx={{ fontSize: 12, flexShrink: 0 }} />}>{lead.phone}</MetaRow>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        {followup && (
          <Box
            title="Next follow-up"
            sx={{
              fontSize: '0.6875rem',
              px: 0.875,
              py: 0.25,
              borderRadius: 5,
              bgcolor: followup.bg,
              color: followup.color,
              fontWeight: followup.bold ? 500 : 400,
              whiteSpace: 'nowrap'
            }}
          >
            {followup.label}
          </Box>
        )}
        <Typography component="span" sx={{ ml: 'auto', fontSize: '0.6875rem', color: 'text.disabled' }}>
          Added {lead.createdAt}
        </Typography>
      </Box>
    </Paper>
  );
};

export default LeadCard;
